import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Mail, MessageCircle, Phone, UserCheck } from "lucide-react";

import { ReferralCard } from "@/components/admin/clientes/ReferralCard";
import { AdminCard, ListState, StatusPill } from "@/components/admin/primitives";
import { formatDate, relativeDays } from "@/lib/dates";
import { countLabel, formatBRL, padOrderNumber } from "@/lib/format";
import { useCustomer, useCustomerOrders } from "@/lib/queries";
import { useSession } from "@/lib/session";

/**
 * Relatorio individual do cliente.
 *
 * Tudo aqui e soma de pedidos, calculada pela API: nao existe cadastro de
 * cliente, entao "cliente desde" e a data do primeiro pedido e "recorrente" e
 * ter mais de um.
 */
export const Route = createFileRoute("/admin/_painel/clientes/$clienteId")({
  head: () => ({ meta: [{ title: "Cliente — Painel MonteBox" }] }),
  component: AdminCliente,
});

const STATUS_LABEL: Record<string, string> = {
  RASCUNHO: "Rascunho",
  SALVO: "Salvo",
  ENVIADO: "Enviado",
  PENDENTE_APROVACAO: "Pendente de aprovação",
  APROVADO: "Aprovado",
  EM_PRODUCAO: "Em produção",
  CONCLUIDO: "Concluído",
  CANCELADO: "Cancelado",
};

const COLUMNS = "min-w-[640px] grid grid-cols-[110px_140px_100px_150px_1fr] items-center gap-4";

function AdminCliente() {
  const { clienteId } = Route.useParams();
  const customer = useCustomer(clienteId);
  const orders = useCustomerOrders(clienteId);
  const { profile } = useSession();

  if (!customer.data) {
    return (
      <AdminCard className="m-8">
        <ListState
          loading={customer.isLoading}
          error={customer.error}
          empty={!customer.isLoading}
          emptyLabel="Cliente não encontrado."
        />
      </AdminCard>
    );
  }

  const data = customer.data;
  const items = orders.data?.items ?? [];
  const mostPieces = Math.max(1, ...data.mix.map((line) => line.pieces));
  // Estados sem nenhum pedido so ocupariam espaco.
  const byStatus = data.byStatus.filter((line) => line.count > 0);

  return (
    <>
      <header className="flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-mb-line bg-white px-4 py-3 sm:px-8">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/clientes"
            aria-label="Voltar para a lista de clientes"
            className="grid h-9 w-9 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
          >
            <ArrowLeft className="h-[18px] w-[18px]" />
          </Link>
          <Link to="/admin/clientes" className="text-sm font-semibold text-mb-muted">
            Clientes
          </Link>
          <ChevronRight className="h-4 w-4 text-mb-muted" />
          <h1 className="font-display text-xl font-bold tracking-tight text-mb-navy">
            {data.name}
          </h1>
        </div>

        <Link
          to="/"
          className="flex items-center gap-1.5 rounded-full bg-mb-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-mb-ink"
        >
          Novo pedido para o cliente
        </Link>
      </header>

      <div className="grid items-start gap-6 p-4 sm:p-8 xl:grid-cols-[1fr_300px]">
        {/* Coluna esquerda — o histórico */}
        <div className="flex flex-col gap-6">
          <AdminCard className="p-6">
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-mb-surface font-display text-base font-bold text-mb-navy">
                {initials(data.name)}
              </span>
              <div className="min-w-0 grow">
                <p className="truncate font-display text-lg font-bold text-mb-navy">{data.name}</p>
                <p className="text-xs text-mb-muted">
                  Primeiro pedido em {formatDate(data.firstOrderAt)} · último{" "}
                  {relativeDays(data.lastOrderAt)}
                </p>
              </div>
              {data.hasAccount && (
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-mb-navy/10 px-3 py-1 text-xs font-bold text-mb-navy">
                  <UserCheck className="h-3.5 w-3.5" />
                  Tem conta
                </span>
              )}
              {data.orders > 1 && (
                <span className="shrink-0 rounded-full bg-mb-yellow/25 px-3 py-1 text-xs font-bold text-mb-ink">
                  Cliente recorrente
                </span>
              )}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
              {[
                { label: "Total gasto", value: formatBRL(data.total) },
                { label: "Pedidos", value: String(data.orders) },
                { label: "Ticket médio", value: formatBRL(data.averageTicket) },
                { label: "Peças no total", value: String(data.pieces) },
              ].map((metric) => (
                <div key={metric.label} className="rounded-mb-field bg-mb-surface px-4 py-3">
                  <p className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted">
                    {metric.label}
                  </p>
                  <p className="mt-1 font-display text-lg font-bold tabular-nums text-mb-navy">
                    {metric.value}
                  </p>
                </div>
              ))}
            </div>
          </AdminCard>

          <AdminCard className="overflow-x-auto">
            <div className="flex items-center justify-between border-b border-mb-line px-6 py-4">
              <h2 className="font-display text-base font-bold text-mb-navy">
                Pedidos deste cliente
              </h2>
              <Link
                to="/admin/pedidos"
                className="text-xs font-bold text-mb-navy hover:text-mb-ink"
              >
                Abrir na lista de pedidos
              </Link>
            </div>

            <div className={`${COLUMNS} border-b border-mb-line bg-mb-surface px-6 py-3`}>
              {["Pedido", "Data", "Peças", "Total", "Status"].map((head) => (
                <span
                  key={head}
                  className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted"
                >
                  {head}
                </span>
              ))}
            </div>

            <ListState
              loading={orders.isLoading}
              error={orders.error}
              empty={items.length === 0}
              emptyLabel="Nenhum pedido."
            />

            {items.map((order) => (
              <Link
                key={order.id}
                to="/admin/pedidos/$pedidoId"
                params={{ pedidoId: order.id }}
                className={`${COLUMNS} border-b border-mb-line px-6 py-3.5 transition-colors last:border-b-0 hover:bg-mb-surface`}
              >
                <span className="font-display text-sm font-bold tabular-nums text-mb-navy">
                  #{padOrderNumber(order.number)}
                </span>
                <span className="text-sm tabular-nums text-mb-muted">
                  {formatDate(order.createdAt)}
                </span>
                <span className="text-sm tabular-nums text-mb-muted">
                  {order.items.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
                <span className="text-sm font-bold tabular-nums text-mb-navy">
                  {formatBRL(order.total)}
                </span>
                <StatusPill status={order.status} className="justify-self-start" />
              </Link>
            ))}
          </AdminCard>

          {data.mix.length > 0 && (
            <AdminCard className="p-6">
              <h2 className="font-display text-base font-bold text-mb-navy">
                O que este cliente costuma pedir
              </h2>
              <p className="mt-1 text-xs text-mb-muted">
                {data.orders === 1
                  ? "Itens do único pedido."
                  : `Somando os itens dos ${data.orders} pedidos.`}
              </p>

              <div className="mt-5 grid gap-8 md:grid-cols-[1fr_300px]">
                <div className="flex flex-col gap-4">
                  {data.mix.map((line) => (
                    <div key={line.label}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-bold text-mb-ink">{line.label}</span>
                        <span className="tabular-nums text-mb-muted">
                          {countLabel(line.pieces, "peça", "peças")}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-mb-surface">
                        <div
                          className="h-full rounded-full bg-mb-navy"
                          style={{ width: `${(line.pieces / mostPieces) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted">
                    Opções mais escolhidas
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {data.topOptions.map((option) => (
                      <span
                        key={option.label}
                        className="rounded-full border border-mb-line bg-white px-3 py-1.5 text-xs font-semibold text-mb-navy"
                      >
                        {option.label} · {option.orders}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </AdminCard>
          )}
        </div>

        {/* Coluna direita — contato e resumo */}
        <div className="flex flex-col gap-6">
          {/* Vínculo mexe em quem recebe comissão: só admin. */}
          {profile?.role === "ADMIN" && <ReferralCard customerKey={clienteId} />}
          <AdminCard className="p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">Contato</h2>

            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm text-mb-ink">
                <Phone className="h-4 w-4 shrink-0 text-mb-muted" />
                {data.whatsapp}
              </div>
              {data.email && (
                <div className="flex items-center gap-2 text-sm text-mb-ink">
                  <Mail className="h-4 w-4 shrink-0 text-mb-muted" />
                  <span className="truncate">{data.email}</span>
                </div>
              )}
            </div>

            <a
              href={`https://wa.me/55${data.key}`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3.5 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-[18px] w-[18px]" />
              Chamar no WhatsApp
            </a>
            <p className="mt-2 text-[11px] leading-4 text-mb-muted">
              Nome, WhatsApp e e-mail vêm do último pedido — não existe cadastro separado.
            </p>
          </AdminCard>

          <AdminCard className="p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">Pedidos por status</h2>
            <div className="mt-4 flex flex-col gap-2">
              {byStatus.map((line) => (
                <div
                  key={line.status}
                  className="flex items-center justify-between rounded-mb-field bg-mb-surface px-4 py-2.5"
                >
                  <span className="text-sm text-mb-muted">
                    {STATUS_LABEL[line.status] ?? line.status}
                  </span>
                  <span className="font-display text-sm font-bold tabular-nums text-mb-navy">
                    {line.count}
                  </span>
                </div>
              ))}
            </div>
          </AdminCard>
        </div>
      </div>
    </>
  );
}

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
